import React, { useState, useEffect } from 'react';
import { Users, UserCheck, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';
import Layout from '@/components/Layout';

import { BACKEND_URL } from '../../config/api';
const MentorAssignments = ({ user, onLogout }) => {
  const [mentors, setMentors] = useState([]);
  const [stats, setStats] = useState({ mentors: [], unassigned_students: 0 });
  const [loading, setLoading] = useState(true);
  const [assigningAll, setAssigningAll] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('lms_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/mentor-assignments/mentor-student-count`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setLoading(false);
    }
  };

  const handleAssignAll = async () => {
    if (!selectedMentor) {
      alert('Please select a mentor');
      return;
    }

    if (!window.confirm(`Are you sure you want to assign ALL unassigned students to ${selectedMentor}?`)) {
      return;
    }

    setAssigningAll(true);
    try {
      const token = localStorage.getItem('lms_token');
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/mentor-assignments/assign-bulk?mentor_email=${selectedMentor}&assign_all=true`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`✅ ${response.data.message}\n${response.data.students_assigned} students assigned successfully!`);
      fetchStats(); // Refresh stats
    } catch (error) {
      console.error('Error assigning students:', error);
      alert(`❌ Failed to assign students: ${error.response?.data?.detail || error.message}`);
    } finally {
      setAssigningAll(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Users className="w-8 h-8 text-blue-600" />
          Mentor-Student Assignments
        </h1>
        <p className="text-gray-600 mt-2">
          Manage student assignments to mentors
        </p>
      </div>

      {/* Bulk Assignment Card */}
      {stats.unassigned_students > 0 && (
        <div className="mb-6 p-6 bg-orange-50 border-2 border-orange-300 rounded-lg">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-6 h-6 text-orange-600" />
            <h3 className="text-lg font-semibold text-orange-900">
              {stats.unassigned_students} students without a mentor
            </h3>
          </div>

          <div className="flex items-center gap-4">
            <select
              value={selectedMentor}
              onChange={(e) => setSelectedMentor(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a mentor to assign all students...</option>
              {stats.mentors.map((mentor) => (
                <option key={mentor.mentor_email} value={mentor.mentor_email}>
                  {mentor.mentor_name} ({mentor.mentor_email}) - Currently: {mentor.student_count} students
                </option>
              ))}
            </select>

            <button
              onClick={handleAssignAll}
              disabled={!selectedMentor || assigningAll}
              className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-semibold flex items-center gap-2 whitespace-nowrap"
            >
              {assigningAll ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Assigning...
                </>
              ) : (
                <>
                  <UserCheck className="w-5 h-5" />
                  Assign All Students
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {stats.unassigned_students === 0 && (
        <div className="mb-6 p-6 bg-green-50 border-2 border-green-300 rounded-lg">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <h3 className="text-lg font-semibold text-green-900">
                All students are assigned!
              </h3>
              <p className="text-green-700 mt-1">
                Every student has a mentor assigned to them.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Mentor Stats Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">Current Assignments</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mentor Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned Students
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stats.mentors.map((mentor, index) => (
                <tr key={mentor.mentor_email} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                        {mentor.mentor_name.charAt(0)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {mentor.mentor_name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">{mentor.mentor_email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex px-4 py-1 rounded-full text-lg font-bold ${
                      mentor.student_count > 0
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      {mentor.student_count}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {mentor.student_count > 0 ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        No Students
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Mentors</p>
              <p className="text-3xl font-bold mt-1">{stats.mentors.length}</p>
            </div>
            <Users className="w-12 h-12 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Assigned Students</p>
              <p className="text-3xl font-bold mt-1">
                {stats.mentors.reduce((sum, m) => sum + m.student_count, 0)}
              </p>
            </div>
            <CheckCircle className="w-12 h-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Unassigned Students</p>
              <p className="text-3xl font-bold mt-1">{stats.unassigned_students}</p>
            </div>
            <AlertCircle className="w-12 h-12 text-orange-200" />
          </div>
        </div>
      </div>
    </div>
    </Layout>
  );
};

export default MentorAssignments;
